import PageHeader from '../../components/PageHeader/PageHeader';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, Modal, OverlayTrigger, Row, Spinner, Tooltip, Alert } from 'react-bootstrap';
import SistemaToast from '../../components/SistemaToast/SistemaToast';
import { useSistemaToast } from '../../components/SistemaToast/useSistemaToast';
import DataTable from 'react-data-table-component';
import { LuCheck, LuUpload } from 'react-icons/lu';
import { urlApiProd } from '../utiles';
import { newManualNota, recalcNota, cleanCnpj, parseNum, tipoNfLabel } from './inventarioCalculos';
import {
  filtrarNotasDuplicadas,
  mensajeDuplicados,
} from './inventarioNfDuplicados';
import {
  applyGestionNota,
  defaultsGestionNota,
  ESTADO_DISPONIBLE,
  ESTADO_EN_PROCESO,
  tipoMovimientoLabel,
} from './inventarioEstados';
import {
  buildColumnasInventario,
  flattenNotasAFilas,
  INVENTARIO_TABLE_STYLES,
  ordenarFilasPorPadreHijo,
  patchFilaEnNota,
} from './inventarioGridUtils';
import {
  enrichNotaConOc,
  medidasFromOcLine,
  ocEstadoLabel,
  ocDisplayLabel,
  ordenesCompraParaSeleccion,
  productoIdsEquivalentes,
  productosParaInventario,
  proveedoresParaInventario,
} from './inventarioOcUtils';
import {
  aplicarComposicionAnterior,
  buscarNfComposicion,
} from './inventarioComposicionAnterior';
import InventarioNfEditModal from './InventarioNfEditModal';
import CrearPackingListDialog from './CrearPackingListDialog';
import {
  aplicarFiltrosGuardadas,
  buildOpcionesFiltroCascadaGuardadas,
  FILTROS_GUARDADAS_VACIOS,
  FilterAutocomplete,
  FilterSelect,
  hayFiltrosActivos,
  MESES_NOMBRE,
  opcionesAnioSelector,
  opcionesDiaSelector,
  opcionesMesSelector,
  TIPOS_MOVIMIENTO,
} from './inventarioFiltrosUtils';

const LABEL_W = { width: '170px' };

function CampoSelect({ id, label, value, onChange, disabled, children }) {
  return (
    <Form.Group>
      <Form.Label htmlFor={id} className="mb-1 small">
        {label}
      </Form.Label>
      <Form.Select id={id} value={value} onChange={onChange} disabled={disabled}>
        {children}
      </Form.Select>
    </Form.Group>
  );
}

function FilterableList({
  id,
  label,
  value,
  onInputChange,
  onSelect,
  options,
  placeholder,
  controlSize,
  labelClassName = 'small mb-1',
  fullWidth = false,
  clearable = false,
  onClear,
}) {
  const [open, setOpen] = useState(false);
  const query = String(value || '').trim().toLowerCase();
  const showClearBtn = clearable && String(value || '').trim() !== '';

  const handleClear = (e) => {
    e.preventDefault();
    setOpen(false);
    if (onClear) onClear();
    else onInputChange('');
  };
  const visibleOptions = useMemo(() => {
    const list = options || [];
    if (!query) return list.slice(0, 80);
    return list.filter((opt) => String(opt.label || '').toLowerCase().includes(query)).slice(0, 80);
  }, [options, query]);

  return (
    <Form.Group
      className="mb-0 inv-filterable"
      style={fullWidth ? { width: '100%', minWidth: 0 } : { minWidth: '220px' }}
    >
      <Form.Label className={labelClassName} htmlFor={id}>
        {label}
      </Form.Label>
      <div className="inv-filterable-input-wrap position-relative">
        <Form.Control
          id={id}
          className={`w-100${showClearBtn ? ' inv-filterable-input--clearable' : ''}`}
          size={controlSize}
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            onInputChange(e.target.value);
            setOpen(true);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
        />
        {showClearBtn ? (
          <button
            type="button"
            className="inv-filterable-clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            aria-label="Limpiar"
            title="Limpiar"
          >
            ×
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="inv-filterable-menu">
          {visibleOptions.length ? (
            visibleOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="inv-filterable-option"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(opt);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            ))
          ) : (
            <div className="inv-filterable-empty">Sin coincidencias</div>
          )}
        </div>
      ) : null}
    </Form.Group>
  );
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
}

function IndeterminateCheckbox({ checked, indeterminate = false, className = '', ...props }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [checked, indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className={`form-check-input ${className}`.trim()}
      checked={checked}
      {...props}
    />
  );
}

function cantidadDetalleLabel(row) {
  const paquetes = parseNum(row.paquetes);
  const m3 = parseNum(row.total_m3);
  if (row._tipoFilaInventario === 'retirada') {
    if (paquetes != null && paquetes > 0) return `${paquetes} paquetes en Packing List`;
    if (m3 != null && m3 > 0) {
      const volumen = m3.toLocaleString('es-CL', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      });
      return `${volumen} m³ en Packing List`;
    }
    return 'Sin saldo disponible';
  }
  if ((paquetes == null || paquetes === 0) && m3 != null && m3 > 0) {
    const volumen = m3.toLocaleString('es-CL', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
    const uso = row._tipoFilaInventario === 'utilizada' ? 'utilizados' : 'disponibles';
    return `${volumen} m³ ${uso}`;
  }
  if (paquetes == null) return 'Paquetes sin dato';
  const piezas = row.pcs_paq == null || row.pcs_paq === '' ? '—' : row.pcs_paq;
  return `${row.paquetes} paquetes x ${piezas} piezas`;
}

function medidaDetalleLabel(row) {
  const parts = [row.espesor, row.ancho, row.largo].filter((v) => v !== '' && v != null);
  return parts.length ? parts.join(' x ') : '';
}

function validarPesosNota(nota) {
  const nro = nota?.nro_nf || '(sin numero)';
  const liquido = parseNum(nota?.peso_liquido);
  const bruto = parseNum(nota?.peso_bruto);
  if (liquido == null || liquido <= 0) return `NF ${nro}: ingrese peso liquido.`;
  if (bruto == null || bruto <= 0) return `NF ${nro}: ingrese peso bruto.`;
  return null;
}

function lineaTieneDimensiones(linea) {
  return ['espesor', 'ancho', 'largo', 'altura_rw', 'castigo_rw'].some(
    (field) => linea?.[field] != null && String(linea[field]).trim() !== ''
  );
}

function validarM3LineasNota(nota) {
  const nro = nota?.nro_nf || '(sin numero)';
  const lineas = nota?.lineas || [];
  for (let i = 0; i < lineas.length; i += 1) {
    const ln = lineas[i];
    if (!lineaTieneDimensiones(ln)) continue;
    if (parseNum(ln.total_m3) == null) {
      return `NF ${nro}: línea ${i + 1} tiene Total M³ en blanco.`;
    }
  }
  return null;
}

function fmtDashboardNum(v, dec = 3) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: dec,
  }).format(n);
}

function fechaOrdenValue(oc) {
  const raw = oc?.emision || oc?.fecha_creacion;
  const d = raw ? new Date(raw) : null;
  return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
}

export default function Inventario() {
  const [loading, setLoading] = useState(true);
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [productosCatalog, setProductosCatalog] = useState([]);
  const [medidasCatalog, setMedidasCatalog] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [calidades, setCalidades] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [puertos, setPuertos] = useState([]);
  const [guardadas, setGuardadas] = useState([]);

  const [idProveedor, setIdProveedor] = useState('');
  const [proveedorXmlTexto, setProveedorXmlTexto] = useState('');
  const [idProducto, setIdProducto] = useState('');
  const [idOrdencompra, setIdOrdencompra] = useState('');
  const [archivosXml, setArchivosXml] = useState([]);
  const [previewNotas, setPreviewNotas] = useState([]);
  const [previewDuplicados, setPreviewDuplicados] = useState([]);
  const [, setParseErrores] = useState([]);
  const [procesando, setProcesando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [copiarComposicionAnterior, setCopiarComposicionAnterior] = useState(false);

  const [editNota, setEditNota] = useState(null);
  const [editSource, setEditSource] = useState(null);
  const [persistiendo, setPersistiendo] = useState(false);
  const [filtrosGuardadas, setFiltrosGuardadas] = useState({ ...FILTROS_GUARDADAS_VACIOS });
  const [dashboardFiltros, setDashboardFiltros] = useState({ idProveedor: '', idOrdencompra: '' });
  const [dashboardProveedorTexto, setDashboardProveedorTexto] = useState('');
  const [dashboardOcTexto, setDashboardOcTexto] = useState('');
  const [dashboardPage, setDashboardPage] = useState(1);
  const [ocCerrar, setOcCerrar] = useState(null);
  const [cerrandoOc, setCerrandoOc] = useState(false);
  /** Selección por parte/línea: Set de "nfId:lineaId" */
  const [selectedLineKeys, setSelectedLineKeys] = useState(() => new Set());
  const [showResumenSeleccion, setShowResumenSeleccion] = useState(false);
  const [showPackingDialog, setShowPackingDialog] = useState(false);
  const packingToast = useSistemaToast();
  const notifySistema = packingToast.notify;
  const fileRef = useRef(null);

  const proveedorSel = proveedores.find((p) => String(p.id_proveedor) === String(idProveedor));
  const ocSel = ordenes.find((o) => String(o.id_ordencompra) === String(idOrdencompra));

  const dashboardProveedores = useMemo(() => {
    const ids = new Set((ordenes || []).map((oc) => Number(oc.proveedor)).filter(Number.isFinite));
    return (proveedores || [])
      .filter((p) => ids.has(Number(p.id_proveedor)))
      .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
  }, [ordenes, proveedores]);

  const dashboardOcs = useMemo(() => {
    return (ordenes || [])
      .filter((oc) => {
        if (dashboardFiltros.idProveedor && Number(oc.proveedor) !== Number(dashboardFiltros.idProveedor)) {
          return false;
        }
        return true;
      })
      .slice()
      .sort((a, b) => fechaOrdenValue(b) - fechaOrdenValue(a));
  }, [ordenes, dashboardFiltros.idProveedor]);

  const dashboardProveedorOptions = useMemo(
    () =>
      dashboardProveedores.map((p) => ({
        value: String(p.id_proveedor),
        label: String(p.nombre || ''),
      })),
    [dashboardProveedores]
  );

  const dashboardOcOptions = useMemo(
    () =>
      dashboardOcs.map((oc) => ({
        value: String(oc.id_ordencompra),
        label: ocDisplayLabel(oc),
      })),
    [dashboardOcs]
  );

  const productoIdsSeleccionados = useMemo(
    () => productoIdsEquivalentes(productosCatalog, idProducto),
    [productosCatalog, idProducto]
  );

  const filtrosSeleccion = useMemo(
    () => ({ idProveedor, idProducto, productoIds: productoIdsSeleccionados, idOrdencompra }),
    [idProveedor, idProducto, productoIdsSeleccionados, idOrdencompra]
  );

  const proveedoresFiltrados = useMemo(
    () => proveedoresParaInventario(ordenes, proveedores, filtrosSeleccion),
    [ordenes, proveedores, filtrosSeleccion]
  );

  const productosFiltrados = useMemo(
    () => productosParaInventario(ordenes, productos, filtrosSeleccion),
    [ordenes, productos, filtrosSeleccion]
  );

  const productoSel =
    productosFiltrados.find((p) =>
      (p.producto_ids || [p.id_producto]).some((id) => Number(id) === Number(idProducto))
    ) || productos.find((p) => String(p.id_producto) === String(idProducto));

  const ocsFiltradas = useMemo(
    () => ordenesCompraParaSeleccion(ordenes, filtrosSeleccion),
    [ordenes, filtrosSeleccion]
  );

  const proveedorXmlOptions = useMemo(
    () =>
      proveedoresFiltrados.map((p) => ({
        value: String(p.id_proveedor),
        label: p.nombre,
      })),
    [proveedoresFiltrados]
  );

  const patchProveedorXml = (valor) => {
    setProveedorXmlTexto(valor);
    setIdProveedor('');
  };

  const selectProveedorXml = (opt) => {
    setProveedorXmlTexto(opt?.label || '');
    setIdProveedor(opt?.value || '');
  };

  const clearProveedorXml = () => {
    setProveedorXmlTexto('');
    setIdProveedor('');
  };

  useEffect(() => {
    if (!ordenes.length) return;

    const provIds = new Set(proveedoresFiltrados.map((p) => Number(p.id_proveedor)));
    const prodIds = new Set(
      productosFiltrados.flatMap((p) =>
        p.producto_ids?.length ? p.producto_ids : [Number(p.id_producto)]
      )
    );
    const ocIds = new Set(ocsFiltradas.map((o) => Number(o.id_ordencompra)));

    if (idProveedor && !provIds.has(Number(idProveedor))) {
      setIdProveedor('');
      setProveedorXmlTexto('');
    }
    if (idProducto && !prodIds.has(Number(idProducto))) {
      setIdProducto('');
    }
    if (idOrdencompra && !ocIds.has(Number(idOrdencompra))) {
      setIdOrdencompra('');
    }
  }, [
    ordenes.length,
    idProveedor,
    idProducto,
    idOrdencompra,
    proveedoresFiltrados,
    productosFiltrados,
    ocsFiltradas,
  ]);

  useEffect(() => {
    if (!dashboardFiltros.idOrdencompra) return;
    const ok = dashboardOcs.some((oc) => String(oc.id_ordencompra) === String(dashboardFiltros.idOrdencompra));
    if (!ok) {
      setDashboardFiltros((prev) => ({ ...prev, idOrdencompra: '' }));
      setDashboardOcTexto('');
    }
  }, [dashboardFiltros.idOrdencompra, dashboardOcs]);

  useEffect(() => {
    setDashboardPage(1);
  }, [dashboardFiltros.idProveedor, dashboardFiltros.idOrdencompra]);

  const ocCatalogos = useMemo(
    () => ({ productosCatalog, medidas: medidasCatalog }),
    [productosCatalog, medidasCatalog]
  );

  const ocMedidas = useMemo(() => {
    const { medidasPorGrado, usdPorGrado, calidades: cals } = medidasFromOcLine(
      ocSel,
      idProducto,
      calidades,
      ocCatalogos
    );
    return { medidasPorGrado, usdPorGrado, calidades: cals };
  }, [ocSel, idProducto, calidades, ocCatalogos]);

  const loadCatalogos = useCallback(async () => {
    const headers = authHeaders();
    if (!headers) return;
    setLoading(true);
    try {
      const [r1, r2, r3, r4, r5, r6, r7, r8, r9] = await Promise.all([
        fetch(`${urlApiProd}/proveedores`, { headers }),
        fetch(`${urlApiProd}/nombreproductos_ordencompra`, { headers }),
        fetch(`${urlApiProd}/ordenescompra`, { headers }),
        fetch(`${urlApiProd}/calidades`, { headers }),
        fetch(`${urlApiProd}/incoterms`, { headers }),
        fetch(`${urlApiProd}/almacenes`, { headers }),
        fetch(`${urlApiProd}/puertos`, { headers }),
        fetch(`${urlApiProd}/productos`, { headers }),
        fetch(`${urlApiProd}/medidas`, { headers }),
      ]);
      if (r1.ok) setProveedores(await r1.json());
      if (r2.ok) setProductos(await r2.json());
      if (r3.ok) setOrdenes(await r3.json());
      if (r4.ok) setCalidades(await r4.json());
      if (r5.ok) setIncoterms(await r5.json());
      if (r6.ok) setAlmacenes(await r6.json());
      if (r7.ok) setPuertos(await r7.json());
      if (r8.ok) setProductosCatalog(await r8.json());
      if (r9.ok) setMedidasCatalog(await r9.json());
    } catch (e) {
      notifySistema(e.message || 'Error cargando catálogos');
    } finally {
      setLoading(false);
    }
  }, [notifySistema]);

  const loadGuardadas = useCallback(async () => {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const res = await fetch(`${urlApiProd}/inventario/nfs`, { headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Error ${res.status} al cargar registros`);
      }
      const rows = await res.json();
      setGuardadas(rows || []);
    } catch (e) {
      notifySistema(e.message || 'Error al cargar registros guardados');
    }
  }, [notifySistema]);

  useEffect(() => {
    loadCatalogos();
    loadGuardadas();
  }, [loadCatalogos, loadGuardadas]);

  const puedeCargar = idProveedor && idProducto && idOrdencompra;

  const onXmlChange = (e) => {
    setArchivosXml(Array.from(e.target.files || []));
    setParseErrores([]);
    setPreviewDuplicados([]);
  };

  const esXmlValido = (texto) => {
    const doc = new DOMParser().parseFromString(texto, 'text/xml');
    return !doc.getElementsByTagName('parsererror').length;
  };

  const procesarXmls = async () => {
    if (!puedeCargar) {
      notifySistema('Seleccione proveedor, producto y OC antes de cargar XML.');
      return;
    }
    if (!archivosXml.length) {
      notifySistema('Seleccione uno o más archivos XML.');
      return;
    }
    const txtInvalidos = [];
    for (const f of archivosXml) {
      if (f.name.toLowerCase().endsWith('.txt') && !esXmlValido(await f.text())) {
        txtInvalidos.push(f.name);
      }
    }
    if (txtInvalidos.length) {
      notifySistema(`Estos archivos .txt no contienen XML válido: ${txtInvalidos.join(', ')}`);
      return;
    }
    const headers = authHeaders();
    if (!headers) return;
    setProcesando(true);
    packingToast.hide();
    try {
      const fd = new FormData();
      archivosXml.forEach((f) => fd.append('archivos', f));
      fd.append('proveedor', idProveedor);
      fd.append('idProductoGeneral', idProducto);
      fd.append('idOrdencompra', idOrdencompra);
      fd.append('productoGeneral', productoSel?.nombre_producto || '');
      const res = await fetch(`${urlApiProd}/inventario/preview-xmls`, {
        method: 'POST',
        headers,
        body: fd,
      });
      const raw = await res.text();
      let body = {};
      try {
        body = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error(
          res.ok
            ? 'El servidor devolvió una respuesta inválida al procesar los XML.'
            : `Error ${res.status}: el servidor no respondió correctamente (¿caída o timeout?).`
        );
      }
      if (!res.ok) throw new Error(body.error || `Error ${res.status}`);
      const nfComposicion = copiarComposicionAnterior
        ? buscarNfComposicion({
            guardadas,
            idProveedor,
            idProducto,
            productoIds: productoIdsSeleccionados,
          })
        : null;
      let lineasCopiadas = 0;
      const notasMapeadas = (body.notas || []).map((n, i) => {
        const base = defaultsGestionNota(
          recalcNota(
            enrichNotaConOc(
              { ...n, cnpj: cleanCnpj(n.cnpj), _key: `${n.archivo_origen}-${i}-${Date.now()}` },
              ordenes,
              calidades,
              ocCatalogos
            )
          ),
          { incoterms }
        );
        if (!nfComposicion) return base;
        const applied = aplicarComposicionAnterior(base, nfComposicion, { modo: 'xml' });
        lineasCopiadas += applied.copiadas;
        return applied.nota;
      });
      const existentes = [...guardadas, ...previewNotas];
      const { aceptadas, duplicados: dupLocales } = filtrarNotasDuplicadas(
        notasMapeadas,
        existentes
      );
      const dupBackend = body.duplicados || [];
      const todosDuplicados = [...dupBackend, ...dupLocales];
      setPreviewDuplicados(todosDuplicados);
      setPreviewNotas((prev) => [...prev, ...aceptadas]);
      setParseErrores(body.errores || []);
      if (body.errores?.length) {
        notifySistema(body.errores.join(' · '));
      }
      if (todosDuplicados.length) {
        notifySistema(mensajeDuplicados(todosDuplicados));
      }
      if (aceptadas.length) {
        const extra = lineasCopiadas
          ? ` Se copiaron ${lineasCopiadas} línea(s) desde NF anterior del mismo producto.`
          : copiarComposicionAnterior
            ? ' No se encontró composición anterior compatible para copiar.'
            : '';
        notifySistema(`${aceptadas.length} NF(s) listas para revisión.${extra}`);
      } else if (body.errores?.length && !todosDuplicados.length) {
        notifySistema('Ningún XML pudo cargarse. Revise los errores indicados.');
      } else if (todosDuplicados.length && !aceptadas.length) {
        notifySistema('Todas las NF del XML están duplicadas y no se agregaron a la vista previa.');
      }
      if (fileRef.current) fileRef.current.value = '';
      setArchivosXml([]);
    } catch (e) {
      notifySistema(e.message || 'Error al procesar XML');
    } finally {
      setProcesando(false);
    }
  };

  const agregarManual = () => {
    if (!puedeCargar) {
      notifySistema('Seleccione proveedor, producto y OC.');
      return;
    }
    const inc = incoterms.find((i) => Number(i.id_incoterm) === Number(ocSel?.incoterm));
    const notaBase = defaultsGestionNota(
      newManualNota({
        proveedor: proveedorSel,
        producto: productoSel,
        oc: ocSel,
        ocExtras: {
          status_oc: ocEstadoLabel(ocSel?.estado),
          calidades: ocMedidas.calidades,
        },
      }),
      { incoterms }
    );
    const nfComposicion = copiarComposicionAnterior
      ? buscarNfComposicion({
          guardadas,
          idProveedor,
          idProducto,
          productoIds: productoIdsSeleccionados,
        })
      : null;
    const applied = nfComposicion
      ? aplicarComposicionAnterior(notaBase, nfComposicion, { modo: 'manual' })
      : { nota: notaBase, copiadas: 0 };
    const nota = applied.nota;
    nota.incoterm_label = inc?.nombre || '';
    nota.calidades_oc = ocMedidas.calidades;
    setPreviewNotas((prev) => [...prev, recalcNota(enrichNotaConOc(nota, ordenes, calidades, ocCatalogos))]);
    if (copiarComposicionAnterior) {
      notifySistema(
        applied.copiadas
          ? `NF manual creada con ${applied.copiadas} línea(s) copiadas desde NF anterior del mismo producto.`
          : 'NF manual creada. No se encontró composición anterior compatible para copiar.'
      );
    }
  };

  const updateNota = (key, updater) => {
    setPreviewNotas((prev) =>
      prev.map((n) => (n._key === key ? recalcNota(typeof updater === 'function' ? updater(n) : updater) : n))
    );
  };

  const quitarNota = (key) => {
    setPreviewNotas((prev) => prev.filter((n) => n._key !== key));
  };

  const guardarTodas = async () => {
    if (!previewNotas.length) return;
    const { aceptadas, duplicados } = filtrarNotasDuplicadas(previewNotas, guardadas);
    if (duplicados.length) {
      setPreviewDuplicados((prev) => [...prev, ...duplicados]);
      notifySistema(mensajeDuplicados(duplicados));
    }
    if (!aceptadas.length) return;
    const pesoError = aceptadas.map(validarPesosNota).find(Boolean);
    if (pesoError) {
      notifySistema(pesoError);
      return;
    }
    const m3Error = aceptadas.map(validarM3LineasNota).find(Boolean);
    if (m3Error) {
      notifySistema(m3Error);
      return;
    }
    const headers = authHeaders();
    if (!headers) return;
    setGuardando(true);
    packingToast.hide();
    const keysGuardadas = new Set(aceptadas.map((n) => n._key));
    try {
      const res = await fetch(`${urlApiProd}/inventario/guardar`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notas: aceptadas.map((n) => defaultsGestionNota(n, { incoterms })),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `Error ${res.status}`);
      notifySistema(`${body.guardadas} NF(s) guardadas en inventario de gestión.`);
      setPreviewNotas((prev) => prev.filter((n) => !keysGuardadas.has(n._key)));
      setPreviewDuplicados([]);
      await loadGuardadas();
    } catch (e) {
      notifySistema(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const updateGuardada = useCallback((id, updater) => {
    setGuardadas((prev) =>
      prev.map((n) =>
        n.id === id
          ? recalcNota(
              enrichNotaConOc(typeof updater === 'function' ? updater(n) : updater, ordenes, calidades, ocCatalogos)
            )
          : n
      )
    );
  }, [ordenes, calidades, ocCatalogos]);

  const abrirEdicion = useCallback(
    (row, source) => {
      const list = source === 'guardada' ? guardadas : previewNotas;
      const n = list.find((x) => (x._key || `nf-${x.id}`) === row._notaKey);
      if (n) {
        setEditNota(
          defaultsGestionNota(recalcNota(enrichNotaConOc({ ...n }, ordenes, calidades, ocCatalogos)), {
            incoterms,
          })
        );
        setEditSource(source);
      }
    },
    [guardadas, previewNotas, ordenes, calidades, ocCatalogos, incoterms]
  );

  const patchGuardadaFila = useCallback(
    async (row, field, value) => {
      if (row._lineIdx !== 0) return;
      const nota = guardadas.find((x) => (x._key || `nf-${x.id}`) === row._notaKey);
      if (!nota?.id) return;

      let updated = patchFilaEnNota(nota, 0, field, value, { incoterms });
      updated = recalcNota(enrichNotaConOc(updated, ordenes, calidades, ocCatalogos));
      updateGuardada(nota.id, updated);

      const headers = authHeaders();
      if (!headers) return;
      try {
        const res = await fetch(`${urlApiProd}/inventario/nfs/${nota.id}`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(updated),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || `Error ${res.status}`);
        updateGuardada(nota.id, defaultsGestionNota(body, { incoterms }));
      } catch (e) {
        notifySistema(e.message || 'Error al actualizar registro');
        await loadGuardadas();
      }
    },
    [guardadas, ordenes, calidades, ocCatalogos, incoterms, loadGuardadas, notifySistema, updateGuardada]
  );

  const patchPreviewFila = (row, field, value) => {
    updateNota(row._notaKey, (n) =>
      recalcNota(patchFilaEnNota(n, row._lineIdx, field, value, { incoterms }))
    );
  };

  const previewEnriquecidas = useMemo(
    () => previewNotas.map((n) => recalcNota(enrichNotaConOc(n, ordenes, calidades, ocCatalogos))),
    [previewNotas, ordenes, calidades, ocCatalogos]
  );

  const guardadasNormalizadas = useMemo(
    () => guardadas.map((n) => defaultsGestionNota(n, { incoterms })),
    [guardadas, incoterms]
  );

  const guardadasEnriquecidas = useMemo(
    () =>
      guardadasNormalizadas.map((n) => recalcNota(enrichNotaConOc(n, ordenes, calidades, ocCatalogos))),
    [guardadasNormalizadas, ordenes, calidades, ocCatalogos]
  );

  const previewFilas = useMemo(() => flattenNotasAFilas(previewEnriquecidas), [previewEnriquecidas]);
  const guardadasFilasBase = useMemo(() => flattenNotasAFilas(guardadasEnriquecidas), [guardadasEnriquecidas]);

  const dashboardRows = useMemo(() => {
    const recibidoPorOc = new Map();
    for (const nf of guardadasEnriquecidas || []) {
      const ocId = nf.id_ordencompra == null ? '' : String(nf.id_ordencompra);
      if (!ocId) continue;
      if (!recibidoPorOc.has(ocId)) {
        recibidoPorOc.set(ocId, {
          m3Recibidos: 0,
          nfIds: new Set(),
          paquetes: 0,
        });
      }
      const acc = recibidoPorOc.get(ocId);
      if (nf.id != null) acc.nfIds.add(String(nf.id));
      else if (nf.nro_nf) acc.nfIds.add(String(nf.nro_nf));
      (nf.lineas || []).forEach((ln) => {
        acc.m3Recibidos += parseNum(ln.total_m3) || 0;
        acc.paquetes += parseNum(ln.paquetes) || 0;
      });
    }

    return dashboardOcs
      .filter((oc) => {
        if (
          dashboardFiltros.idOrdencompra &&
          Number(oc.id_ordencompra) !== Number(dashboardFiltros.idOrdencompra)
        ) {
          return false;
        }
        return true;
      })
      .map((oc) => {
        const ocId = String(oc.id_ordencompra);
        const recibido = recibidoPorOc.get(ocId) || { m3Recibidos: 0, nfIds: new Set(), paquetes: 0 };
        const m3Oc = (oc.m3 || []).reduce((sum, v) => sum + (parseNum(v) || 0), 0);
        return {
          id: oc.id_ordencompra,
          oc,
          ocLabel: ocDisplayLabel(oc),
          proveedor:
            proveedores.find((p) => Number(p.id_proveedor) === Number(oc.proveedor))?.nombre || '',
          m3Oc,
          m3Recibidos: recibido.m3Recibidos,
          saldo: m3Oc - recibido.m3Recibidos,
          nfRecibidas: recibido.nfIds.size,
          paquetesRecibidos: recibido.paquetes,
          cerrada: Number(oc.estado) === 4,
        };
      });
  }, [
    guardadasEnriquecidas,
    dashboardOcs,
    dashboardFiltros.idOrdencompra,
    proveedores,
  ]);

  const dashboardPageSize = 10;
  const dashboardTotalPages = Math.max(1, Math.ceil(dashboardRows.length / dashboardPageSize));
  const dashboardRowsPagina = useMemo(() => {
    const start = (dashboardPage - 1) * dashboardPageSize;
    return dashboardRows.slice(start, start + dashboardPageSize);
  }, [dashboardRows, dashboardPage]);
  const dashboardPageOptions = useMemo(() => {
    const start = Math.max(1, dashboardPage - 2);
    const end = Math.min(dashboardTotalPages, dashboardPage + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [dashboardPage, dashboardTotalPages]);

  useEffect(() => {
    if (dashboardPage > dashboardTotalPages) setDashboardPage(dashboardTotalPages);
  }, [dashboardPage, dashboardTotalPages]);

  const filtrosGuardadasActivos = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filtrosGuardadas).map(([k, v]) => [k, String(v || '').trim()])
      ),
    [filtrosGuardadas]
  );

  const guardadasFilas = useMemo(
    () => aplicarFiltrosGuardadas(guardadasFilasBase, filtrosGuardadasActivos),
    [guardadasFilasBase, filtrosGuardadasActivos]
  );

  const guardadasFilasOrdenadas = useMemo(
    () => ordenarFilasPorPadreHijo(guardadasFilas),
    [guardadasFilas]
  );

  const opcionesFiltroGuardadas = useMemo(
    () => buildOpcionesFiltroCascadaGuardadas(guardadasFilasBase, filtrosGuardadasActivos),
    [guardadasFilasBase, filtrosGuardadasActivos]
  );

  const mesesFiltroGuardadas = useMemo(
    () => opcionesMesSelector(opcionesFiltroGuardadas.meses),
    [opcionesFiltroGuardadas.meses]
  );

  const diasFiltroGuardadas = useMemo(
    () => opcionesDiaSelector(opcionesFiltroGuardadas.dias),
    [opcionesFiltroGuardadas.dias]
  );

  const aniosFiltroGuardadas = useMemo(
    () => opcionesAnioSelector(opcionesFiltroGuardadas.anios),
    [opcionesFiltroGuardadas.anios]
  );

  const patchFiltroGuardada = (campo, valor) => {
    setFiltrosGuardadas((prev) => ({ ...prev, [campo]: valor }));
  };

  const limpiarFiltrosGuardadas = () => {
    setFiltrosGuardadas({ ...FILTROS_GUARDADAS_VACIOS });
  };

  const patchDashboardProveedor = (valor) => {
    setDashboardProveedorTexto(valor);
    setDashboardOcTexto('');
    setDashboardFiltros((prev) => ({
      ...prev,
      idProveedor: '',
      idOrdencompra: '',
    }));
  };

  const selectDashboardProveedor = (opt) => {
    setDashboardProveedorTexto(opt?.label || '');
    setDashboardOcTexto('');
    setDashboardFiltros((prev) => ({
      ...prev,
      idProveedor: opt?.value || '',
      idOrdencompra: '',
    }));
  };

  const patchDashboardOc = (valor) => {
    setDashboardOcTexto(valor);
    setDashboardFiltros((prev) => ({
      ...prev,
      idOrdencompra: '',
    }));
  };

  const selectDashboardOc = (opt) => {
    setDashboardOcTexto(opt?.label || '');
    setDashboardFiltros((prev) => ({
      ...prev,
      idOrdencompra: opt?.value || '',
    }));
  };

  const confirmarCerrarOc = async () => {
    if (!ocCerrar?.id_ordencompra) return;
    const headers = authHeaders();
    if (!headers) return;
    setCerrandoOc(true);
    packingToast.hide();
    try {
      const payload = { ...ocCerrar, estado: 4 };
      const res = await fetch(`${urlApiProd}/editarordencompra/${ocCerrar.id_ordencompra}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Error ${res.status} al cerrar OC`);
      setOrdenes((prev) =>
        prev.map((oc) =>
          Number(oc.id_ordencompra) === Number(ocCerrar.id_ordencompra)
            ? { ...oc, estado: 4, editado: (oc.editado || 0) + 1 }
            : oc
        )
      );
      packingToast.notify(`OC ${ocDisplayLabel(ocCerrar)} cerrada.`);
      setOcCerrar(null);
    } catch (e) {
      notifySistema(e.message || 'Error al cerrar OC');
    } finally {
      setCerrandoOc(false);
    }
  };

  const columnasPreview = useMemo(
    () =>
      buildColumnasInventario({
        editable: true,
        onPatch: patchPreviewFila,
        onEdit: (row) => abrirEdicion(row, 'preview'),
        onQuitar: (row) => quitarNota(row._notaKey),
        almacenes,
        puertos,
        incoterms,
        proveedores,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewNotas, almacenes, puertos, incoterms, proveedores, abrirEdicion]
  );

  const lineKeyOf = useCallback((row) => {
    if (row?._rowKey) return row._rowKey;
    if (row?._notaId == null || row?._lineaId == null) return null;
    return `${row._notaId}:${row._lineaId}`;
  }, []);

  const lineaSeleccionable = useCallback((row) => {
    if (row?._notaId == null || row?._lineaId == null) return false;
    if (row?._activa === false || row?._tipoFilaInventario === 'retirada') return false;
    const est = String(row._estadoMercaderia || row.estado_mercaderia || ESTADO_DISPONIBLE);
    return est === ESTADO_DISPONIBLE;
  }, []);

  const lineKeysSeleccionablesVisibles = useMemo(() => {
    const keys = [];
    for (const row of guardadasFilas) {
      if (!lineaSeleccionable(row)) continue;
      const k = lineKeyOf(row);
      if (k) keys.push(k);
    }
    return keys;
  }, [guardadasFilas, lineaSeleccionable, lineKeyOf]);

  const allSelectedVisible =
    lineKeysSeleccionablesVisibles.length > 0 &&
    lineKeysSeleccionablesVisibles.every((k) => selectedLineKeys.has(k));

  const filasSeleccionadas = useMemo(
    () =>
      guardadasFilasBase.filter((row) => {
        if (!lineaSeleccionable(row)) return false;
        const key = lineKeyOf(row);
        return key != null && selectedLineKeys.has(key);
      }),
    [guardadasFilasBase, lineaSeleccionable, lineKeyOf, selectedLineKeys]
  );

  const toggleLinea = useCallback(
    (row) => {
      const k = lineKeyOf(row);
      if (!k || !lineaSeleccionable(row)) return;
      setSelectedLineKeys((prev) => {
        const next = new Set(prev);
        if (next.has(k)) next.delete(k);
        else next.add(k);
        return next;
      });
    },
    [lineKeyOf, lineaSeleccionable]
  );

  const toggleSelectAll = useCallback(() => {
    setSelectedLineKeys((prev) => {
      const next = new Set(prev);
      const allOn =
        lineKeysSeleccionablesVisibles.length > 0 &&
        lineKeysSeleccionablesVisibles.every((k) => next.has(k));
      if (allOn) {
        lineKeysSeleccionablesVisibles.forEach((k) => next.delete(k));
      } else {
        lineKeysSeleccionablesVisibles.forEach((k) => next.add(k));
      }
      return next;
    });
  }, [lineKeysSeleccionablesVisibles]);

  const resumenSeleccion = useMemo(() => {
    const notas = new Set();
    let totalM3 = 0;
    let totalBrl = 0;
    let lineasSinM3 = 0;
    let lineasSinBrl = 0;

    for (const row of filasSeleccionadas) {
      if (row._notaId != null) notas.add(String(row._notaId));
      const m3 = parseNum(row.total_m3);
      const brl = parseNum(row.total_brl_m3);
      if (m3 == null) lineasSinM3 += 1;
      else totalM3 += m3;
      if (brl == null) lineasSinBrl += 1;
      else totalBrl += brl;
    }

    return {
      lineas: filasSeleccionadas.length,
      notas: notas.size,
      totalM3,
      totalBrl,
      lineasSinM3,
      lineasSinBrl,
    };
  }, [filasSeleccionadas]);

  const seleccionPacking = useMemo(
    () => {
      const seen = new Set();
      return filasSeleccionadas.flatMap((row) => {
        if (row._notaId == null) return [];
        const sourceIds = row._lineaIds?.length ? row._lineaIds : [row._lineaId];
        return sourceIds
          .map((sourceId) => ({
            id_inventario_nf: Number(row._notaId),
            id_inventario_linea: Number(sourceId),
          }))
          .filter((linea) => {
            const key = `${linea.id_inventario_nf}:${linea.id_inventario_linea}`;
            if (!Number.isFinite(linea.id_inventario_nf) || !Number.isFinite(linea.id_inventario_linea) || seen.has(key)) {
              return false;
            }
            seen.add(key);
            return true;
          });
      });
    },
    [filasSeleccionadas]
  );

  const guardadasConditionalRowStyles = useMemo(
    () => [
      {
        when: (row) => row._tipoFilaInventario === 'utilizada',
        style: { backgroundColor: 'rgba(108, 117, 125, 0.08)' },
      },
      {
        when: (row) =>
          row._tipoFilaInventario === 'saldo_liberado' ||
          row._tipoFilaInventario === 'saldo_disponible',
        style: { backgroundColor: 'rgba(25, 135, 84, 0.08)' },
      },
      {
        when: (row) => {
          if (!lineaSeleccionable(row)) return false;
          const k = lineKeyOf(row);
          return k != null && selectedLineKeys.has(k);
        },
        style: {
          backgroundColor: 'rgba(13, 110, 253, 0.11)',
          boxShadow: 'inset 3px 0 0 rgba(13, 110, 253, 0.55)',
        },
        classNames: ['inv-row-selected'],
      },
    ],
    [lineKeyOf, lineaSeleccionable, selectedLineKeys]
  );

  const columnasGuardadas = useMemo(() => {
    const base = buildColumnasInventario({
      editable: false,
      editableGestion: true,
      includeGestion: true,
      onPatch: patchGuardadaFila,
      onEdit: (row) => {
        if (row._guardada) abrirEdicion(row, 'guardada');
      },
      almacenes,
      puertos,
      incoterms,
      proveedores,
    });
    const checkCol = {
      name: (
        <div className="inv-nf-tree-head">
          <IndeterminateCheckbox
            checked={allSelectedVisible}
            onChange={toggleSelectAll}
            title="Seleccionar todas las partes disponibles"
            aria-label="Seleccionar todas las partes disponibles"
            disabled={lineKeysSeleccionablesVisibles.length === 0}
          />
          <span>Composición</span>
        </div>
      ),
      width: '210px',
      minWidth: '210px',
      ignoreRowClick: true,
      cell: (row) => {
        if (row._tipoFilaInventario === 'retirada') {
          return (
            <div className="inv-nf-tree-row">
              <span
                className="d-inline-flex align-items-center justify-content-center inv-nf-tree-status text-danger fw-bold"
                title="Esta composición fue retirada de la NF y ya no puede asignarse a un Packing List nuevo."
                aria-label="Composición retirada"
              >
                !
              </span>
              <div className="inv-nf-tree-text">
                <span className="badge bg-danger-subtle text-danger-emphasis mb-1">
                  Composición retirada
                </span>
                {row.revision_pendiente ? (
                  <span className="badge bg-warning text-dark mb-1">Revisión pendiente</span>
                ) : null}
                <span>{cantidadDetalleLabel(row)}</span>
                <small className="text-muted">
                  {[row.grado, medidaDetalleLabel(row)].filter(Boolean).join(' · ')}
                </small>
              </div>
            </div>
          );
        }
        if (!lineaSeleccionable(row)) {
          const est = String(row.estado_mercaderia || '');
          if (est === ESTADO_EN_PROCESO) {
            return (
              <div className="inv-nf-tree-row">
                <span
                  className="d-inline-flex align-items-center justify-content-center inv-nf-tree-status"
                  title={row.revision_pendiente ? 'Composición por revisar en un Packing List' : 'Ya utilizada en packing list (En proceso)'}
                  aria-label={row.revision_pendiente ? 'Revisión pendiente de Packing List' : 'Ya utilizada en packing list'}
                >
                  {row.revision_pendiente ? (
                    <span className="text-danger fw-bold">!</span>
                  ) : (
                    <LuCheck size={16} color="#198754" strokeWidth={3} />
                  )}
                </span>
                <div className="inv-nf-tree-text">
                  {row.revision_pendiente ? (
                    <span className="badge bg-warning text-dark mb-1">Revisión pendiente</span>
                  ) : row._tipoFilaInventario === 'utilizada' ? (
                    <span className="badge bg-secondary-subtle text-secondary-emphasis mb-1">
                      En Packing List
                    </span>
                  ) : null}
                  <span>{cantidadDetalleLabel(row)}</span>
                  <small className="text-muted">
                    {[row.grado, medidaDetalleLabel(row)].filter(Boolean).join(' · ')}
                  </small>
                </div>
              </div>
            );
          }
          return null;
        }
        const k = lineKeyOf(row);
        return (
          <div className="inv-nf-tree-row">
            <IndeterminateCheckbox
              checked={k ? selectedLineKeys.has(k) : false}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                toggleLinea(row);
              }}
              aria-label={`Seleccionar NF ${row.nro_nf} línea ${(row._sourceLineIdx ?? row._lineIdx) + 1}`}
            />
            <div className="inv-nf-tree-text">
              {row._tipoFilaInventario === 'saldo_liberado' ? (
                <span
                  className="badge bg-success-subtle text-success-emphasis mb-1"
                  title={
                    row.fecha_liberacion
                      ? `Liberado desde esta composición · actualizado ${row.fecha_liberacion}`
                      : 'Saldo asociado a la composición original'
                  }
                >
                  Saldo liberado · reutilizable
                </span>
              ) : row._tipoFilaInventario === 'saldo_disponible' ? (
                <span className="badge bg-light text-success border mb-1">Saldo disponible</span>
              ) : null}
              <span>{cantidadDetalleLabel(row)}</span>
              <small className="text-muted">
                {[row.grado, medidaDetalleLabel(row)].filter(Boolean).join(' · ')}
              </small>
            </div>
          </div>
        );
      },
    };
    return [checkCol, ...base];
  }, [
    almacenes,
    puertos,
    incoterms,
    proveedores,
    abrirEdicion,
    patchGuardadaFila,
    selectedLineKeys,
    allSelectedVisible,
    toggleSelectAll,
    toggleLinea,
    lineaSeleccionable,
    lineKeyOf,
    lineKeysSeleccionablesVisibles.length,
  ]);

  const persistirEdicion = async (nota) => {
    const pesoError = validarPesosNota(nota);
    if (pesoError) {
      notifySistema(pesoError);
      return;
    }
    const m3Error = validarM3LineasNota(nota);
    if (m3Error) {
      notifySistema(m3Error);
      return;
    }
    if (editSource === 'preview') {
      const otras = [
        ...guardadas,
        ...previewNotas.filter((n) => n._key !== nota._key),
      ];
      const { aceptadas, duplicados } = filtrarNotasDuplicadas([nota], otras);
      if (!aceptadas.length) {
        notifySistema(mensajeDuplicados(duplicados) || 'NF duplicada.');
        return;
      }
      updateNota(nota._key, nota);
      setEditNota(null);
      setEditSource(null);
      return;
    }
    const headers = authHeaders();
    if (!headers) return;
    setPersistiendo(true);
    packingToast.hide();
    try {
      const res = await fetch(`${urlApiProd}/inventario/nfs/${nota.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(applyGestionNota(nota, {}, { incoterms })),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `Error ${res.status}`);
      updateGuardada(nota.id, defaultsGestionNota(body, { incoterms }));
      notifySistema('Registro actualizado.');
      setEditNota(null);
      setEditSource(null);
    } catch (e) {
      notifySistema(e.message || 'Error al actualizar');
    } finally {
      setPersistiendo(false);
    }
  };

  return (
    <div className="container-fluid ui-page">
      <style>{`
        .inv-grid-cell { width: 100%; min-width: 0; }
        .inv-grid-input {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          font-size: 0.8rem;
          box-sizing: border-box;
        }
        .inv-grid-cell .pt-date-input { width: 100%; }
        .inv-grid-cell .pt-date-input__field { font-size: 0.8rem; }
        .inv-grid-input.inv-grid-incoterm { min-width: 0; }
        .inv-grid-input.inv-grid-num { text-align: center; }
        .inv-grid-num-cell { width: 100%; text-align: center; }
        .inv-dashboard-wrap {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #f8fafc;
          padding: 0.75rem;
          margin-bottom: 0.9rem;
        }
        .inv-dashboard-title {
          font-size: 0.9rem;
          font-weight: 700;
          color: #0f172a;
        }
        .inv-dashboard-table {
          margin-bottom: 0;
          background: #fff;
        }
        .inv-dashboard-table th,
        .inv-dashboard-table td {
          font-size: 0.78rem;
          vertical-align: middle;
          white-space: nowrap;
        }
        .inv-dashboard-saldo-pos {
          color: #166534;
          font-weight: 700;
        }
        .inv-dashboard-saldo-neg {
          color: #b91c1c;
          font-weight: 700;
        }
        .inv-filterable {
          position: relative;
        }
        .inv-filterable-input-wrap {
          width: 100%;
        }
        .inv-filterable-input--clearable {
          padding-right: 2rem;
        }
        .inv-filterable-clear {
          position: absolute;
          top: 50%;
          right: 0.35rem;
          transform: translateY(-50%);
          border: none;
          background: transparent;
          color: #6c757d;
          font-size: 1.15rem;
          line-height: 1;
          padding: 0 0.25rem;
          cursor: pointer;
        }
        .inv-filterable-clear:hover {
          color: #212529;
        }
        .inv-filterable-menu {
          position: absolute;
          z-index: 30;
          top: calc(100% + 2px);
          left: 0;
          right: 0;
          max-height: 240px;
          overflow-y: auto;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          background: #fff;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.14);
        }
        .inv-filterable-option {
          display: block;
          width: 100%;
          border: 0;
          border-bottom: 1px solid #f1f5f9;
          background: #fff;
          padding: 0.35rem 0.5rem;
          text-align: left;
          font-size: 0.82rem;
        }
        .inv-filterable-option:hover,
        .inv-filterable-option:focus {
          background: #eaf2ff;
        }
        .inv-filterable-empty {
          padding: 0.45rem 0.5rem;
          color: #64748b;
          font-size: 0.82rem;
        }
        .inv-nf-tree-head,
        .inv-nf-tree-row {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          width: 100%;
          min-width: 0;
        }
        .inv-nf-tree-head {
          font-weight: 600;
          line-height: 1.15;
        }
        .inv-nf-tree-text {
          display: flex;
          flex-direction: column;
          min-width: 0;
          line-height: 1.15;
        }
        .inv-nf-tree-text > span,
        .inv-nf-tree-text > strong,
        .inv-nf-tree-text > small {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .inv-nf-tree-status {
          width: 1rem;
          min-width: 1rem;
        }
        .inv-nf-link {
          font-size: inherit;
          line-height: 1.15;
          text-align: left;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .rdt_TableBody .inv-row-selected,
        .rdt_TableBody .inv-row-selected:hover {
          background-color: rgba(13, 110, 253, 0.11) !important;
          box-shadow: inset 3px 0 0 rgba(13, 110, 253, 0.55);
        }
        .rdt_TableBody .inv-row-selected .rdt_TableCell {
          background-color: transparent !important;
        }
        .rdt_TableCell { font-size: 0.8rem; }
        .rdt_TableHeadRow { font-size: 0.75rem; }
      `}</style>
      <InventarioNfEditModal
        show={!!editNota}
        nota={editNota}
        almacenes={almacenes}
        puertos={puertos}
        incoterms={incoterms}
        proveedores={proveedores}
        onClose={() => {
          setEditNota(null);
          setEditSource(null);
        }}
        onSave={(updated) => {
          if (editSource === 'preview') {
            updateNota(editNota._key, updated);
          } else if (editSource === 'guardada') {
            updateGuardada(editNota.id, updated);
          }
          setEditNota(updated);
        }}
        onPersist={persistirEdicion}
        persistLabel={editSource === 'guardada' ? 'Guardar' : 'Aplicar'}
        persisting={persistiendo}
      />

      <Modal
        show={showResumenSeleccion}
        onHide={() => setShowResumenSeleccion(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Detalle de la selección</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap gap-3 mb-3">
            <span><strong>Líneas:</strong> {resumenSeleccion.lineas}</span>
            <span><strong>Notas Fiscales:</strong> {resumenSeleccion.notas}</span>
            <span>
              <strong>M³:</strong> {fmtDashboardNum(resumenSeleccion.totalM3, 3)}
              {resumenSeleccion.lineasSinM3 > 0 ? ' (parcial)' : ''}
            </span>
            <span>
              <strong>R$:</strong> {fmtDashboardNum(resumenSeleccion.totalBrl, 2)}
              {resumenSeleccion.lineasSinBrl > 0 ? ' (parcial)' : ''}
            </span>
          </div>
          {resumenSeleccion.lineasSinM3 > 0 || resumenSeleccion.lineasSinBrl > 0 ? (
            <Alert variant="warning" className="py-2 small">
              Sin dato en {resumenSeleccion.lineasSinM3 > 0
                ? `${resumenSeleccion.lineasSinM3} ${resumenSeleccion.lineasSinM3 === 1 ? 'línea' : 'líneas'} para M³`
                : ''}
              {resumenSeleccion.lineasSinM3 > 0 && resumenSeleccion.lineasSinBrl > 0 ? ' y ' : ''}
              {resumenSeleccion.lineasSinBrl > 0
                ? `${resumenSeleccion.lineasSinBrl} ${resumenSeleccion.lineasSinBrl === 1 ? 'línea' : 'líneas'} para R$`
                : ''}
              . Los totales correspondientes son parciales.
            </Alert>
          ) : null}
          <div className="table-responsive">
            <table className="table table-sm table-striped table-bordered align-middle small mb-0">
              <thead>
                <tr>
                  <th>Nota Fiscal</th>
                  <th>Proveedor</th>
                  <th>Grado</th>
                  <th>Medida</th>
                  <th className="text-end">M³</th>
                  <th className="text-end">Valor (R$)</th>
                </tr>
              </thead>
              <tbody>
                {filasSeleccionadas.map((row) => {
                  const m3 = parseNum(row.total_m3);
                  const brl = parseNum(row.total_brl_m3);
                  const medida = [row.espesor, row.ancho, row.largo]
                    .filter((v) => v != null && v !== '')
                    .join(' × ');
                  return (
                    <tr key={lineKeyOf(row)}>
                      <td>{row.nro_nf || '—'}</td>
                      <td>{row.proveedor || '—'}</td>
                      <td>{row.grado || '—'}</td>
                      <td>{medida || '—'}</td>
                      <td className="text-end">{m3 == null ? 'Sin dato' : fmtDashboardNum(m3, 3)}</td>
                      <td className="text-end">{brl == null ? 'Sin dato' : fmtDashboardNum(brl, 2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResumenSeleccion(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <CrearPackingListDialog
        show={showPackingDialog}
        onHide={() => setShowPackingDialog(false)}
        seleccionInicial={seleccionPacking}
        notasDisponibles={guardadasEnriquecidas}
        onSaved={(data, meta = {}) => {
          loadGuardadas();
          if (meta.redirecting) return;
          setSelectedLineKeys(new Set());
          if (meta.emitirExcel) return;
          packingToast.notify(
            data?.nombre
              ? `Packing list "${data.nombre}" creado con éxito`
              : 'Packing list creado con éxito'
          );
        }}
      />

      <Modal
        show={!!ocCerrar}
        onHide={() => {
          if (!cerrandoOc) setOcCerrar(null);
        }}
        centered
      >
        <Modal.Header closeButton={!cerrandoOc}>
          <Modal.Title>Cerrar OC</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Usted está seguro de cerrar esta OC?
          {ocCerrar ? (
            <div className="text-muted small mt-2">OC {ocDisplayLabel(ocCerrar)}</div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={cerrandoOc}
            onClick={() => setOcCerrar(null)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={cerrandoOc}
            onClick={confirmarCerrarOc}
          >
            {cerrandoOc ? 'Cerrando…' : 'Cerrar OC'}
          </button>
        </Modal.Footer>
      </Modal>

      <SistemaToast
        show={packingToast.show}
        message={packingToast.message}
        onClose={packingToast.hide}
      />

      <PageHeader eyebrow="Logística" title="Inventario" descripcion="Carga de notas fiscales y XML, y preparación de la información para los packing lists." />
      <div className="card">
        <div className="card-body mb-2">
          {loading ? (
            <>
              <div className="text-center">
                <Spinner animation="border" role="status" />
              </div>
              <div className="text-center">
                <span className="sr-only">Cargando...</span>
              </div>
            </>
          ) : (
            <>
              <div className="card mb-3">
                <div className="card-body py-3">
                  <p className="text-muted small mb-3">
                    Los listados de proveedor, producto y OC se filtran entre sí según las órdenes de compra
                    disponibles. Luego cargue XMLs de nota fiscal o ingrese una NF manualmente.
                  </p>

                  <Row className="g-2 mb-3 align-items-end">
                    <Col md={4}>
                      <FilterableList
                        id="inv-proveedor"
                        label="Proveedor"
                        labelClassName="mb-1 small"
                        fullWidth
                        value={proveedorXmlTexto}
                        onInputChange={patchProveedorXml}
                        onSelect={selectProveedorXml}
                        options={proveedorXmlOptions}
                        placeholder="Buscar proveedor…"
                        clearable
                        onClear={clearProveedorXml}
                      />
                    </Col>
                    <Col md={4}>
                      <CampoSelect
                        id="inv-oc"
                        label="Orden de compra"
                        value={idOrdencompra}
                        onChange={(e) => {
                          const val = e.target.value;
                          setIdOrdencompra(val);
                          if (!val) return;
                          const oc = ordenes.find((o) => String(o.id_ordencompra) === val);
                          if (oc?.proveedor != null) {
                            const pid = String(oc.proveedor);
                            setIdProveedor(pid);
                            const prov = proveedores.find((p) => String(p.id_proveedor) === pid);
                            setProveedorXmlTexto(prov?.nombre || '');
                          }
                        }}
                      >
                        <option value="">Seleccione OC…</option>
                        {ocsFiltradas.map((oc) => (
                          <option key={oc.id_ordencompra} value={oc.id_ordencompra}>
                            {ocDisplayLabel(oc)}
                          </option>
                        ))}
                      </CampoSelect>
                    </Col>
                    <Col md={4}>
                      <CampoSelect
                        id="inv-producto"
                        label="Producto"
                        value={idProducto}
                        onChange={(e) => setIdProducto(e.target.value)}
                      >
                        <option value="">Seleccione producto…</option>
                        {productosFiltrados.map((p) => (
                          <option
                            key={p.id_producto}
                            value={
                              p.producto_ids?.some((id) => Number(id) === Number(idProducto))
                                ? idProducto
                                : p.id_producto
                            }
                          >
                            {p.nombre_producto}
                          </option>
                        ))}
                      </CampoSelect>
                    </Col>
                  </Row>

                  <div className="d-flex align-items-center flex-nowrap mb-0">
                    <div
                      className="input-group flex-shrink-0"
                      style={{ width: 'auto', maxWidth: 'calc(170px + 450px)' }}
                    >
                      <label htmlFor="inv-xml" className="input-group-text" style={LABEL_W}>
                        XMLs de NF:
                      </label>
                      <input
                        ref={fileRef}
                        id="inv-xml"
                        type="file"
                        className="form-control"
                        style={{ width: 450, maxWidth: '50vw' }}
                        accept=".xml,.txt"
                        multiple
                        disabled={!puedeCargar || procesando}
                        onChange={onXmlChange}
                      />
                    </div>
                    <div className="d-flex flex-nowrap gap-2 ms-3 flex-shrink-0">
                      <button
                        type="button"
                        className="btn btn-outline-primary text-nowrap"
                        disabled={!puedeCargar || procesando || !archivosXml.length}
                        onClick={procesarXmls}
                      >
                        <LuUpload className="me-1" aria-hidden />
                        {procesando ? 'Procesando…' : 'Cargar XMLs'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary text-nowrap"
                        disabled={!puedeCargar}
                        onClick={agregarManual}
                      >
                        Agregar NF manual
                      </button>
                    </div>
                  </div>
                  <Form.Check
                    id="inv-copiar-composicion-anterior"
                    className="mt-2 mb-0"
                    type="checkbox"
                    checked={copiarComposicionAnterior}
                    disabled={!puedeCargar}
                    onChange={(e) => setCopiarComposicionAnterior(e.target.checked)}
                    label="Usar composición anterior del mismo producto"
                  />
                </div>
              </div>

              {previewNotas.length > 0 || previewDuplicados.length > 0 ? (
                <>
                  {previewDuplicados.length > 0 ? (
                    <Alert variant="warning" className="py-2 small mb-2">
                      <strong>{mensajeDuplicados(previewDuplicados)}</strong>
                      <ul className="mb-0 mt-1 ps-3">
                        {previewDuplicados.map((msg) => (
                          <li key={msg}>{msg}</li>
                        ))}
                      </ul>
                    </Alert>
                  ) : null}
                  {previewNotas.length > 0 ? (
                <>
                  <div className="input-group mb-3">
                    <span className="input-group-text flex-grow-1 justify-content-between">
                      <span>
                        Vista previa — <strong>{previewNotas.length}</strong> NF ·{' '}
                        <strong>{previewFilas.length}</strong> fila(s) pendiente(s) de guardar
                      </span>
                      <span className="text-muted small ms-2">
                        Amarillo: NF desde XML · Rojo: CNPJ no coincide con el proveedor
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-success"
                      disabled={guardando}
                      onClick={guardarTodas}
                    >
                      {guardando ? 'Guardando…' : 'Guardar todo'}
                    </button>
                  </div>
                  <div className="card mb-3">
                    <div className="card-body">
                      <DataTable
                        keyField="_rowKey"
                        columns={columnasPreview}
                        data={previewFilas}
                        customStyles={INVENTARIO_TABLE_STYLES}
                        noHeader
                        responsive
                        dense
                        highlightOnHover
                        fixedHeader
                        fixedHeaderScrollHeight="480px"
                        pagination={previewFilas.length > 15}
                        paginationPerPage={15}
                        conditionalRowStyles={[
                          {
                            when: (row) =>
                              row.archivo_origen && row.archivo_origen !== 'manual',
                            style: { backgroundColor: 'rgba(255, 193, 7, 0.18)' },
                          },
                          {
                            when: (row) => row.cnpj_match === false,
                            style: { backgroundColor: 'rgba(220, 53, 69, 0.2)' },
                          },
                        ]}
                        noDataComponent={
                          <div className="p-4 text-muted text-center">No hay notas en vista previa</div>
                        }
                      />
                    </div>
                  </div>
                </>
                  ) : null}
                </>
              ) : null}

              <div className="card">
                <div className="card-header py-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <h6 className="mb-0">
                      Registros guardados
                      {guardadasFilasBase.length > 0 ? (
                        <span className="text-muted fw-normal small ms-2">
                          {guardadasFilas.length} fila(s)
                          {hayFiltrosActivos(filtrosGuardadasActivos)
                            ? ` de ${guardadasFilasBase.length}`
                            : ''}
                        </span>
                      ) : null}
                    </h6>
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {resumenSeleccion.lineas > 0 ? (
                      <div
                        className="d-flex flex-wrap align-items-center gap-2 small"
                        role="status"
                        aria-live="polite"
                      >
                        <strong>Resumen:</strong>
                        <span className="badge bg-success">
                          {resumenSeleccion.lineas}{' '}
                          {resumenSeleccion.lineas === 1 ? 'línea' : 'líneas'} ·{' '}
                          {resumenSeleccion.notas} NF
                        </span>
                        <span>
                          <strong>M³:</strong> {fmtDashboardNum(resumenSeleccion.totalM3, 3)}
                          {resumenSeleccion.lineasSinM3 > 0 ? ' (parcial)' : ''}
                        </span>
                        <span>
                          <strong>R$:</strong> {fmtDashboardNum(resumenSeleccion.totalBrl, 2)}
                          {resumenSeleccion.lineasSinBrl > 0 ? ' (parcial)' : ''}
                        </span>
                        {resumenSeleccion.lineasSinM3 > 0 || resumenSeleccion.lineasSinBrl > 0 ? (
                          <span className="text-muted">
                            Sin dato: {resumenSeleccion.lineasSinM3 > 0
                              ? `${resumenSeleccion.lineasSinM3} ${resumenSeleccion.lineasSinM3 === 1 ? 'línea' : 'líneas'} sin M³`
                              : ''}
                            {resumenSeleccion.lineasSinM3 > 0 && resumenSeleccion.lineasSinBrl > 0
                              ? ' · '
                              : ''}
                            {resumenSeleccion.lineasSinBrl > 0
                              ? `${resumenSeleccion.lineasSinBrl} ${resumenSeleccion.lineasSinBrl === 1 ? 'línea' : 'líneas'} sin R$`
                              : ''}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    {seleccionPacking.length > 0 ? (
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm"
                        onClick={() => setShowResumenSeleccion(true)}
                      >
                        Vista previa de packing list
                      </button>
                    ) : null}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-crear-packing-list" className="inv-tooltip">
                          Selecciona una o más líneas
                        </Tooltip>
                      }
                    >
                      <span className="d-inline-block">
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          disabled={seleccionPacking.length === 0}
                          onClick={() => setShowPackingDialog(true)}
                          style={seleccionPacking.length === 0 ? { pointerEvents: 'none' } : undefined}
                        >
                          Crear Packing List
                        </button>
                      </span>
                    </OverlayTrigger>
                    {hayFiltrosActivos(filtrosGuardadasActivos) ? (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={limpiarFiltrosGuardadas}
                      >
                        Limpiar filtros
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="card-body pb-2">
                  <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                    <FilterAutocomplete
                      label="N° NF"
                      idSuffix="nf"
                      value={filtrosGuardadas.nf}
                      onChange={(v) => patchFiltroGuardada('nf', v)}
                      options={opcionesFiltroGuardadas.nfs}
                      placeholder="Buscar NF…"
                    />
                    <FilterSelect
                      label="Día emisión NF"
                      idSuffix="dia"
                      value={filtrosGuardadas.dia}
                      onChange={(v) => patchFiltroGuardada('dia', v)}
                      options={diasFiltroGuardadas}
                      emptyLabel="Cualquier día"
                    />
                    <FilterSelect
                      label="Mes emisión NF"
                      idSuffix="mes"
                      value={filtrosGuardadas.mes}
                      onChange={(v) => patchFiltroGuardada('mes', v)}
                      options={mesesFiltroGuardadas}
                      emptyLabel="Cualquier mes"
                      renderOption={(m) => MESES_NOMBRE.find((x) => x.value === m)?.label || m}
                    />
                    <FilterSelect
                      label="Año emisión NF"
                      idSuffix="anio"
                      value={filtrosGuardadas.anio}
                      onChange={(v) => patchFiltroGuardada('anio', v)}
                      options={aniosFiltroGuardadas}
                      emptyLabel="Cualquier año"
                    />
                    <FilterAutocomplete
                      label="OC"
                      idSuffix="oc"
                      value={filtrosGuardadas.oc}
                      onChange={(v) => patchFiltroGuardada('oc', v)}
                      options={opcionesFiltroGuardadas.ocs}
                      placeholder="Orden de compra…"
                    />
                    <FilterSelect
                      label="Tipo NF"
                      idSuffix="tipo-nf"
                      value={filtrosGuardadas.tipoNf}
                      onChange={(v) => patchFiltroGuardada('tipoNf', v)}
                      options={[
                        ...new Set(['0', '1', ...(opcionesFiltroGuardadas.tiposNf || [])]),
                      ]}
                      emptyLabel="Todos"
                      renderOption={(v) => tipoNfLabel(v)}
                    />
                    <FilterAutocomplete
                      label="Grado"
                      idSuffix="grado"
                      value={filtrosGuardadas.grado}
                      onChange={(v) => patchFiltroGuardada('grado', v)}
                      options={opcionesFiltroGuardadas.grados}
                      placeholder="Grado…"
                    />
                    <FilterAutocomplete
                      label="Espesor"
                      idSuffix="espesor"
                      value={filtrosGuardadas.espesor}
                      onChange={(v) => patchFiltroGuardada('espesor', v)}
                      options={opcionesFiltroGuardadas.espesores}
                      placeholder="Espesor…"
                    />
                    <FilterAutocomplete
                      label="Ancho"
                      idSuffix="ancho"
                      value={filtrosGuardadas.ancho}
                      onChange={(v) => patchFiltroGuardada('ancho', v)}
                      options={opcionesFiltroGuardadas.anchos}
                      placeholder="Ancho…"
                    />
                    <FilterAutocomplete
                      label="Largo"
                      idSuffix="largo"
                      value={filtrosGuardadas.largo}
                      onChange={(v) => patchFiltroGuardada('largo', v)}
                      options={opcionesFiltroGuardadas.largos}
                      placeholder="Largo…"
                    />
                    <FilterAutocomplete
                      label="Proveedor"
                      idSuffix="proveedor"
                      value={filtrosGuardadas.proveedor}
                      onChange={(v) => patchFiltroGuardada('proveedor', v)}
                      options={opcionesFiltroGuardadas.proveedores}
                      placeholder="Proveedor…"
                    />
                    <FilterAutocomplete
                      label="Almacén"
                      idSuffix="almacen"
                      value={filtrosGuardadas.almacen}
                      onChange={(v) => patchFiltroGuardada('almacen', v)}
                      options={opcionesFiltroGuardadas.almacenes}
                      placeholder="Almacén…"
                    />
                    <FilterSelect
                      label="Tipo movimiento"
                      idSuffix="tipo"
                      value={filtrosGuardadas.tipoMovimiento}
                      onChange={(v) => patchFiltroGuardada('tipoMovimiento', v)}
                      options={[
                        ...new Set([
                          ...TIPOS_MOVIMIENTO.map((t) => t.value),
                          ...opcionesFiltroGuardadas.tiposMovimiento,
                        ]),
                      ]}
                      emptyLabel="Todos"
                      renderOption={(v) => tipoMovimientoLabel(v)}
                    />
                    <FilterSelect
                      label="Disponibilidad producto"
                      idSuffix="disp"
                      value={filtrosGuardadas.disponibilidadProducto}
                      onChange={(v) => patchFiltroGuardada('disponibilidadProducto', v)}
                      options={opcionesFiltroGuardadas.disponibilidades}
                      emptyLabel="Todas"
                    />
                    <FilterAutocomplete
                      label="Producto"
                      idSuffix="producto"
                      value={filtrosGuardadas.producto}
                      onChange={(v) => patchFiltroGuardada('producto', v)}
                      options={opcionesFiltroGuardadas.productos}
                      placeholder="Producto concatenado…"
                    />
                    <FilterAutocomplete
                      label="Booking"
                      idSuffix="booking"
                      value={filtrosGuardadas.booking}
                      onChange={(v) => patchFiltroGuardada('booking', v)}
                      options={opcionesFiltroGuardadas.bookings}
                      placeholder="Booking…"
                    />
                  </div>
                  <DataTable
                    keyField="_rowKey"
                    columns={columnasGuardadas}
                    data={guardadasFilasOrdenadas}
                    customStyles={INVENTARIO_TABLE_STYLES}
                    noHeader
                    responsive
                    dense
                    highlightOnHover
                    pointerOnHover
                    conditionalRowStyles={guardadasConditionalRowStyles}
                    fixedHeader
                    fixedHeaderScrollHeight="480px"
                    pagination
                    paginationPerPage={30}
                    noDataComponent={
                      <div className="p-4 text-muted text-center">
                        {guardadasFilasBase.length > 0 && hayFiltrosActivos(filtrosGuardadasActivos)
                          ? 'Ningún registro coincide con los filtros'
                          : 'No hay registros guardados'}
                      </div>
                    }
                  />
                </div>
              </div>

              <div className="card mt-3">
                <div className="card-header py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <div>
                    <h6 className="mb-0">Dashboard OC / NF</h6>
                    <div className="text-muted small">
                      Resumen histórico de OC emitidas y recepción en notas fiscales.
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 align-items-end">
                    <FilterableList
                      id="inv-dashboard-proveedor"
                      label="Proveedor"
                      controlSize="sm"
                      value={dashboardProveedorTexto}
                      onInputChange={patchDashboardProveedor}
                      onSelect={selectDashboardProveedor}
                      options={dashboardProveedorOptions}
                      placeholder="Todos"
                    />
                    <FilterableList
                      id="inv-dashboard-oc"
                      label="OC"
                      controlSize="sm"
                      value={dashboardOcTexto}
                      onInputChange={patchDashboardOc}
                      onSelect={selectDashboardOc}
                      options={dashboardOcOptions}
                      placeholder="Todas"
                    />
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered inv-dashboard-table">
                      <thead className="table-light">
                        <tr>
                          <th>OC seleccionada</th>
                          <th>Proveedor</th>
                          <th className="text-end">M3</th>
                          <th className="text-end">M3 recibidos en NF</th>
                          <th className="text-end">Saldo</th>
                          <th className="text-end">NF recibidas</th>
                          <th className="text-end">Paquetes recibidos</th>
                          <th className="text-end">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardRows.length ? (
                          dashboardRowsPagina.map((row) => (
                            <tr key={row.id}>
                              <td>{row.ocLabel}</td>
                              <td>{row.proveedor || '-'}</td>
                              <td className="text-end">{fmtDashboardNum(row.m3Oc)}</td>
                              <td className="text-end">{fmtDashboardNum(row.m3Recibidos)}</td>
                              <td
                                className={`text-end ${
                                  row.saldo < 0 ? 'inv-dashboard-saldo-neg' : 'inv-dashboard-saldo-pos'
                                }`}
                              >
                                {fmtDashboardNum(row.saldo)}
                              </td>
                              <td className="text-end">{row.nfRecibidas}</td>
                              <td className="text-end">{fmtDashboardNum(row.paquetesRecibidos, 0)}</td>
                              <td className="text-end">
                                {row.cerrada ? (
                                  <span className="badge text-bg-secondary">Cerrada</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => setOcCerrar(row.oc)}
                                  >
                                    cerrar
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={8} className="text-center text-muted py-3">
                              Sin OC para los filtros seleccionados.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {dashboardRows.length > dashboardPageSize ? (
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2">
                      <div className="text-muted small">
                        Mostrando {(dashboardPage - 1) * dashboardPageSize + 1}-
                        {Math.min(dashboardPage * dashboardPageSize, dashboardRows.length)} de{' '}
                        {dashboardRows.length}
                      </div>
                      <div className="btn-group btn-group-sm" role="group" aria-label="Paginación dashboard OC NF">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={dashboardPage <= 1}
                          onClick={() => setDashboardPage((p) => Math.max(1, p - 1))}
                        >
                          Anterior
                        </button>
                        {dashboardPageOptions[0] > 1 ? (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setDashboardPage(1)}
                          >
                            1
                          </button>
                        ) : null}
                        {dashboardPageOptions[0] > 2 ? (
                          <button type="button" className="btn btn-outline-secondary" disabled>
                            …
                          </button>
                        ) : null}
                        {dashboardPageOptions.map((page) => (
                          <button
                            key={page}
                            type="button"
                            className={`btn ${page === dashboardPage ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setDashboardPage(page)}
                          >
                            {page}
                          </button>
                        ))}
                        {dashboardPageOptions[dashboardPageOptions.length - 1] < dashboardTotalPages - 1 ? (
                          <button type="button" className="btn btn-outline-secondary" disabled>
                            …
                          </button>
                        ) : null}
                        {dashboardPageOptions[dashboardPageOptions.length - 1] < dashboardTotalPages ? (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setDashboardPage(dashboardTotalPages)}
                          >
                            {dashboardTotalPages}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={dashboardPage >= dashboardTotalPages}
                          onClick={() => setDashboardPage((p) => Math.min(dashboardTotalPages, p + 1))}
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
